import { useSelector } from 'react-redux';

import classes from './classes.module.scss';
import QuickEditTextField from './QuickEditTextField';
import QuickEditSelect from './QuickEditSelect';
import QuickEditAutocomplete from './QuickEditAutocomplete';
import Thumbnail from './Thumbnail';

import {
  getLocalizedDateTime,
  getLocalizedDate,
  getLocalizedTime,
  formatBytes,
  formatPercent
} from 'shared/utils/Utils';

import {
  formatError
} from 'app/utils/valueFormatters';

import {
  formatCurrency
} from 'shared/services/PriceService';

const Cell = props => {
  const {
    colDef,
    data,
    value,
    //quickEdit
  } = props;

  const user = useSelector(state => state.userReducer.user);
  const darkMode = useSelector(state => state.portalReducer.darkMode);

  // render thumbnail if set
  let thumbnail = null;
  let showThumbnail = colDef.thumbnail && !colDef.quickEditActive;
  if (showThumbnail) {
    thumbnail = <Thumbnail colDef={colDef} data={data} />;
  }

  const formatValue = (field, val) => {
    let formatType = field.formatType || field.type;

    switch (formatType) {
      case 'datetime':
        return getLocalizedDateTime(user.locale, val, field.dateStyle, field.timeStyle);

      case 'date':
        return getLocalizedDate(user.locale, val, field.dateStyle);

      case 'time':
        return getLocalizedTime(user.locale, val, field.timeStyle);

      case 'bytes':
        return formatBytes(val);

      case 'currency':
        let currency;
        if (field.getCurrency) {
          currency = field.getCurrency(data);
        } else {
          currency = field.currency || 'USD';
        }

        return formatCurrency(val, currency);

      case 'percent':
        return formatPercent(val);

      default:
        return val;
    }
  }

  // if renderValues is set then render the listed values,
  // unless quick edit mode is active
  if (colDef.renderValues && !colDef.quickEditActive) {
    // if data is null then just return null
    if (!data) {
      return null;
    }

    // as of now we're assuming there are only 2 values,
    // with the top field being bold (ex: job name and event_id)
    let values = colDef.renderValues(data) || [];

    let components = values.map((val, i) => {
      let key = 'p-' + i;

      // if top field then bold
      if (i === 0) {
        let style = { fontWeight: 500, margin: '0.8rem 0' };
        return <p key={key} style={style}>{val}</p>;
      }

      // assume this is the 2nd field, style to nest under first
      let style = { float: 'left', marginTop: '-1.5rem', fontSize: '0.9rem' };
      return <span key={key} style={style}>{val}</span>;
    });

    return (
      <div>
        {thumbnail}
        {components}
      </div>
    );
  }

  // if renderFields is set then render the listed fields,
  // unless quick edit mode is active
  if (colDef.renderFields && !colDef.quickEditActive) {
    // as of now we're assuming there are only 2 fields,
    // with the top field being bold (ex: job name and event_id)

    // if data is null then just return null
    if (!data) {
      return null;
    }

    let values = colDef.renderFields.map((field, i) => {
      let key, value;

      if (typeof field === 'object') {
        key = data.id + field.field + i;

        let val = data[field.field];
        value = formatValue(field, val);

        if (field.prefix) {
          value = field.prefix + value;
        }

      } else {
        key = data.id + field + i;

        // support one level of nested fields via dot notation.
        // this can be expanded as needed
        if (field.match(/\./)) {
          let levels = field.split('.');
          if (data[levels[0]]) {
            value = data[levels[0]][levels[1]];
          }
        } else {
          value = data[field];
        }
      }

      return { key, value };
    });

    let components = values.map(({ key, value }, i) => {
      // if top field then bold
      if (i === 0) {
        let style = { fontWeight: 500, margin: '0.8rem 0' };
        return <p key={key} style={style}>{value}</p>;
      }

      // assume this is the 2nd field, style to nest under first
      let style = { float: 'left', marginTop: '-1.5rem', fontSize: '0.9rem' };
      return <span key={key} style={style}>{value}</span>;
    });

    return (
      <div>
        {thumbnail}
        {components}
      </div>
    );
  }

  // if value is null and quick edit isn't active
  // then the grid is loading, show an empty row
  if (value === null && !colDef.quickEditActive) {
    return null;
  }

  // prepare formatted value
  let valueFormatted = value;

  // format value if formatType is set
  if (colDef.formatType) {
    valueFormatted = formatValue(colDef, value);
  }

  // format value if valueFormatter is set
  if (colDef.valueFormatter) {
    valueFormatted = colDef.valueFormatter(value);
  }

  // format value if dataValueFormatter is set
  if (colDef.dataValueFormatter) {
    valueFormatted = colDef.dataValueFormatter(data, value);
  }

  // if quickEdit isn't activated for the cell then return
  if (!colDef.quickEdit || !colDef.quickEditActive) {
    let style = {
      float: 'left',
      padding: '0.65rem',
      ...colDef.style
    };

    // TODO: I don't believe thumbnail & colDef styles are used anymore in this context
    // Leaving here for now
    if (thumbnail || colDef.style) {
      return (
        <div>
          {thumbnail}
          {valueFormatted && <span style={style}>{valueFormatted}</span>}
        </div>
      );
    } else {
      return valueFormatted
    }

  }

  let inputColor = 'rgb(243 243 248)'; // secondary MUI theme color

  // render quickEdit input
  let inputProps = {
    size: 'small',
    margin: 'none',
    variant: 'filled',
    onChange: e => {
      props.quickEdit.setChange(data.id, colDef.field, e.target.value);

      if (colDef.inputType === 'select') {
        props.quickEdit.closeSelect();
      }
    },
    defaultValue: value ?? '',
    id: `${data.id}-${colDef.field}`,
    sx: {
      margin: '0.45rem 0',
      input: {
        padding: '1rem',
        background: inputColor
      },
      '& .MuiSelect-select': {
        paddingTop: '0.7rem',
        background: inputColor
      }
    },
    InputProps: {
      disableUnderline: true
    },
    SelectProps: {
      sx: {
      }
    }
  }

  // dark mode needs to be completed if desired
  //if (!darkMode) {
  //  inputProps.sx.input.background = '#f3f3fe';
  //  inputProps.SelectProps.sx.background = '#f3f3fe';
  //}

  let formControlProps = {
    sx: { width: '100%' }
  }

  if (data.error) {
    inputProps.error = true;
    inputProps.title = formatError(data.error);
  }

  switch (colDef.inputType) {
    case 'select':
      return (
        <QuickEditSelect
          quickEdit={props.quickEdit}
          formControlProps={formControlProps}
          inputProps={inputProps}
          inputOptions={colDef.inputOptions}
        />
      );

    case 'autocomplete':
      return (
        <QuickEditAutocomplete
          inputProps={inputProps}
          inputOptions={colDef.inputOptions}
          getInputOptions={colDef.getInputOptions}
          selectedOptionFormatter={colDef.selectedOptionFormatter}
          data={data}
          value={value}
        />
      );

    default:
      return (
        <QuickEditTextField
          formControlProps={formControlProps}
          inputProps={inputProps}
        />
      );
  }
}

export default Cell;
